import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { SoldOutNightsEditor } from "@/components/soldOut/SoldOutNightsEditor";
import { Button } from "@/components/ui/Button";
import { FormHint } from "@/pages/stammdaten/schnittstellen/FormHint";

interface TenantSettings {
  integrations: {
    tourist_integration?: {
      enabled?: boolean;
      confirmation_email?: string | null;
      summary_email?: string | null;
      sold_out_nights?: string[];
      sold_out_message?: string | null;
    };
  };
}

interface TouristIntegrationPanelProps {
  canWrite: boolean;
}

export function TouristIntegrationPanel({ canWrite }: TouristIntegrationPanelProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [touristEnabled, setTouristEnabled] = useState(false);
  const [touristConfirmationEmail, setTouristConfirmationEmail] = useState("");
  const [touristSummaryEmail, setTouristSummaryEmail] = useState("");
  const [soldOutNights, setSoldOutNights] = useState<string[]>([]);
  const [newSoldOutDate, setNewSoldOutDate] = useState("");
  const [soldOutMessage, setSoldOutMessage] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .get<TenantSettings>("/api/v1/settings")
      .then((settings) => {
        const tourist = settings.integrations?.tourist_integration;
        setTouristEnabled(tourist?.enabled ?? false);
        setTouristConfirmationEmail(tourist?.confirmation_email ?? "");
        setTouristSummaryEmail(tourist?.summary_email ?? "");
        setSoldOutNights(tourist?.sold_out_nights ?? []);
        setSoldOutMessage(tourist?.sold_out_message ?? "");
        setNewSoldOutDate("");
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function onSave(e: React.FormEvent) {
    e.preventDefault();
    if (!canWrite) return;
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      await api.patch("/api/v1/settings/integrations", {
        tourist_integration_enabled: touristEnabled,
        ...(touristConfirmationEmail.trim()
          ? { tourist_confirmation_email: touristConfirmationEmail.trim() }
          : { clear_tourist_confirmation_email: true }),
        ...(touristSummaryEmail.trim()
          ? { tourist_summary_email: touristSummaryEmail.trim() }
          : { clear_tourist_summary_email: true }),
        tourist_sold_out_nights: soldOutNights,
        ...(soldOutMessage.trim() ? { tourist_sold_out_message: soldOutMessage.trim() } : {}),
      });
      setSuccess(true);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }

  function onAddSoldOutDate() {
    if (!newSoldOutDate || soldOutNights.includes(newSoldOutDate)) return;
    setSoldOutNights((prev) => [...prev, newSoldOutDate].sort());
    setNewSoldOutDate("");
  }

  function onRemoveSoldOutDate(iso: string) {
    setSoldOutNights((prev) => prev.filter((d) => d !== iso));
  }

  if (loading) {
    return <p className="text-sm text-label-secondary">Laden…</p>;
  }

  return (
    <form onSubmit={(e) => void onSave(e)} className="space-y-5">
      <FormHint>
        Bei konfigurierten Empfängeradressen werden pro Online-Buchung bis zu drei E-Mails
        versendet: PMS-Import-XML, formatierte Reservierungsbestätigung und reine
        Buchungszusammenfassung. Die Tourist-Anbindung deaktiviert zusätzlich die automatische
        Verfügbarkeitsprüfung und aktiviert die Ausgebucht-Tage.
      </FormHint>

      <fieldset className="space-y-4" disabled={!canWrite}>
        <label className="flex min-w-0 items-start gap-3 rounded-xl border border-separator bg-fill-quaternary p-4 text-sm">
          <input
            type="checkbox"
            className="mt-0.5 shrink-0"
            checked={touristEnabled}
            onChange={(e) => setTouristEnabled(e.target.checked)}
          />
          <span>
            <span className="font-medium text-label">Tourist-Anbindung aktiv</span>
            <span className="mt-1 block text-xs leading-relaxed text-label-secondary">
              Deaktiviert die automatische Verfügbarkeitsprüfung und aktiviert die Ausgebucht-Tage
              sowie die Tourist-E-Mail-Benachrichtigungen.
            </span>
          </span>
        </label>

        <label className="block min-w-0 text-sm">
          <span className="font-medium text-label">Reservierungsbestätigung E-Mail</span>
          <input
            type="email"
            className="eh-input mt-1.5 max-w-md"
            value={touristConfirmationEmail}
            onChange={(e) => setTouristConfirmationEmail(e.target.value)}
            placeholder="bestaetigung@example.com"
            autoComplete="off"
          />
          <span className="mt-1 block text-xs text-label-secondary">
            Empfänger für die formatierte HTML-Reservierungsbestätigung mit allen Buchungsdaten.
          </span>
        </label>

        <label className="block min-w-0 text-sm">
          <span className="font-medium text-label">Zusammenfassungs E-Mail</span>
          <input
            type="email"
            className="eh-input mt-1.5 max-w-md"
            value={touristSummaryEmail}
            onChange={(e) => setTouristSummaryEmail(e.target.value)}
            placeholder="zusammenfassung@example.com"
            autoComplete="off"
          />
          <span className="mt-1 block text-xs text-label-secondary">
            Empfänger für eine reine Text-Zusammenfassung aller Buchungsangaben ohne Zusatztext.
          </span>
        </label>

        <SoldOutNightsEditor
          soldOutNights={soldOutNights}
          soldOutMessage={soldOutMessage}
          newSoldOutDate={newSoldOutDate}
          canWrite={canWrite}
          onSoldOutMessageChange={setSoldOutMessage}
          onNewSoldOutDateChange={setNewSoldOutDate}
          onAddSoldOutDate={onAddSoldOutDate}
          onRemoveSoldOutDate={onRemoveSoldOutDate}
        />
      </fieldset>

      {error ? <p className="text-sm text-status-red">{error}</p> : null}
      {success ? <p className="text-sm text-status-green">Einstellungen gespeichert.</p> : null}
      {canWrite ? (
        <div className="flex justify-end border-t border-separator pt-4">
          <Button type="submit" loading={saving}>
            Speichern
          </Button>
        </div>
      ) : null}
    </form>
  );
}
